import { apiClient } from "./client";
import { openBlobInNewTab } from "../lib/openBlobInNewTab";
import { requireOnlineAttentionRegister } from "../lib/attentionRegister";
import type {
  GRNCreatePayload,
  GRNDetail,
  GRNListQueryParams,
  GRNListResponse,
  GRNSupplementalReceiptPayload,
} from "../types/grn";
import type { DocumentExtractionEnvelope } from "../types/documentExtraction";

interface DataEnvelope<TData> {
  data: TData;
}

interface GrnReadOptions {
  returnedVesselId?: string;
  invoiceId?: string;
  captureCandidate?: boolean;
  attention?: "L15";
  vesselId?: string;
}

function attentionReadParams(options?: GrnReadOptions): { attention: "L15"; vessel_id: string } | undefined {
  if (!options?.attention) return undefined;
  requireOnlineAttentionRegister(options.attention);
  if (!options.vesselId?.trim()) throw new Error("A valid vessel selection is required.");
  return { attention: options.attention, vessel_id: options.vesselId };
}

export async function listGrns(params: GRNListQueryParams = {}): Promise<GRNListResponse> {
  requireOnlineAttentionRegister(params.attention);
  const response = await apiClient.get<GRNListResponse>("/purchase/grns/", { params });
  return response.data;
}

export async function getReturnedGrnAccess(vesselId?: string, signal?: AbortSignal): Promise<{vessel_ids: string[]}> {
  const response = await apiClient.get<DataEnvelope<{vessel_ids: string[]}>>("/purchase/grns/returned-access/", {
    params: vesselId === undefined ? undefined : {vessel_id: vesselId}, signal,
  });
  const data = response.data.data;
  if (!Array.isArray(data?.vessel_ids) || data.vessel_ids.some(id => typeof id !== "string" || !id)) {
    throw new Error("Returned GRN access is unavailable.");
  }
  return data;
}

export async function getGrn(grnId: string, options?: GrnReadOptions): Promise<DataEnvelope<GRNDetail>> {
  const attentionParams = attentionReadParams(options);
  const response = options?.invoiceId
    ? await apiClient.get<DataEnvelope<GRNDetail>>(`/purchase/grns/${grnId}/`, {
        params: { invoice_id: options.invoiceId },
      })
    : options?.captureCandidate
      ? await apiClient.get<DataEnvelope<GRNDetail>>(`/purchase/grns/${grnId}/`, {
          params: { invoice_capture: "1" },
        })
    : attentionParams
      ? await apiClient.get<DataEnvelope<GRNDetail>>(`/purchase/grns/${grnId}/`, { params: attentionParams })
      : options?.returnedVesselId
        ? await apiClient.get<DataEnvelope<GRNDetail>>(`/purchase/grns/${grnId}/`, {
            params: {correction_state: "RETURNED", vessel_id: options.returnedVesselId},
          })
        : await apiClient.get<DataEnvelope<GRNDetail>>(`/purchase/grns/${grnId}/`);
  return response.data;
}

export async function openGrnDeliveryNote(grnId: string, options?: GrnReadOptions): Promise<void> {
  const params = attentionReadParams(options);
  const response = await apiClient.get<Blob>(`/purchase/grns/${grnId}/delivery-note/`, {
    responseType: "blob",
    ...(params ? { params } : {}),
  });
  openBlobInNewTab(response.data);
}

export async function openGrnDeliveryHistoryNote(grnId: string, eventId: string, options?: GrnReadOptions): Promise<void> {
  const params = attentionReadParams(options);
  const response = await apiClient.get<Blob>(`/purchase/grns/${grnId}/delivery-history/${eventId}/delivery-note/`, {
    responseType: "blob",
    ...(params ? { params } : {}),
  });
  openBlobInNewTab(response.data);
}

export async function createGrn(payload: GRNCreatePayload): Promise<DataEnvelope<GRNDetail>> {
  const formData = new globalThis.FormData();
  formData.append("purchase_order_id", payload.purchase_order_id);
  formData.append("delivery_date", payload.delivery_date ?? "");
  formData.append("delivery_location", payload.delivery_location ?? "");
  formData.append("remarks", payload.remarks ?? "");
  const serializedItems = payload.items.map((item, itemIndex) => {
    const photoKeys =
      item.damage_photos?.map((photo, photoIndex) => {
        const key = `item${itemIndex}_photo_${photoIndex}`;
        formData.append(key, photo);
        return key;
      }) ?? [];

    return {
      po_line_item_id: item.po_line_item_id,
      received_qty: item.received_qty,
      condition: item.condition,
      remarks: item.remarks,
      accept_into_inventory: item.accept_into_inventory ?? false,
      stock_condition: item.stock_condition,
      photo_keys: photoKeys,
    };
  });
  formData.append("items", JSON.stringify(serializedItems));
  formData.append("delivery_note_file", payload.delivery_note_file);

  const response = await apiClient.post<DataEnvelope<GRNDetail>>("/purchase/grns/", formData);
  return response.data;
}

export async function addSupplementalGrnReceipt(
  grnId: string,
  payload: GRNSupplementalReceiptPayload,
): Promise<DataEnvelope<GRNDetail>> {
  const formData = new globalThis.FormData();
  formData.append("delivery_date", payload.delivery_date ?? "");
  formData.append("delivery_location", payload.delivery_location ?? "");
  formData.append("remarks", payload.remarks ?? "");
  const serializedItems = payload.items.map((item, itemIndex) => {
    const photoKeys =
      item.damage_photos?.map((photo, photoIndex) => {
        const key = `supplemental_item${itemIndex}_photo_${photoIndex}`;
        formData.append(key, photo);
        return key;
      }) ?? [];

    return {
      po_line_item_id: item.po_line_item_id,
      additional_qty: item.additional_qty,
      condition: item.condition,
      remarks: item.remarks,
      accept_into_inventory: item.accept_into_inventory ?? false,
      stock_condition: item.stock_condition,
      photo_keys: photoKeys,
    };
  });
  formData.append("items", JSON.stringify(serializedItems));
  formData.append("delivery_note_file", payload.delivery_note_file);

  const response = await apiClient.post<DataEnvelope<GRNDetail>>(
    `/purchase/grns/${grnId}/supplemental-receipts/`,
    formData,
  );
  return response.data;
}

export async function extractGrnPreview(
  document: globalThis.File,
  purchaseOrderId: string,
): Promise<DocumentExtractionEnvelope> {
  const formData = new globalThis.FormData();
  formData.set("document", document);
  formData.set("purchase_order_id", purchaseOrderId);
  const response = await apiClient.post<DocumentExtractionEnvelope>(
    "/purchase/grns/extract-preview/",
    formData,
  );
  return response.data;
}

export async function validateGrnDeliveryNote(document: File, purchaseOrderId: string, signal?: AbortSignal): Promise<DataEnvelope<{ valid: boolean }>> {
  if (!navigator.onLine) throw new Error("Connect to validate the delivery note.");
  const body = new FormData();
  body.set("document", document);
  body.set("purchase_order_id", purchaseOrderId);
  const response = await apiClient.post<DataEnvelope<{ valid: boolean }>>(
    "/purchase/grns/validate-delivery-note/", body, { signal },
  );
  if (response.data.data.valid !== true) throw new Error("Delivery note validation was not confirmed. Please retry.");
  return response.data;
}
